import { useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../../tokens";
import { useAuthStore } from "../../store";
import { JobDetail } from "./JobDetail";

// ── Types ─────────────────────────────────────────────────────────────────

type Inventory = {
  totalJobs: number; activeJobs: number; inactiveJobs: number;
  enrichedJobs: number; weakJobs: number; embeddingCoverage: number;
  freshJobs: number; staleJobs: number;
  byTrust: { trust: string | null; count: number }[];
  bySources: { source: string; count: number }[];
  topCompanies: { company: string; count: number }[];
};

type JobRow = {
  id: string; title: string; company: string; source: string;
  location: string | null; isActive: boolean; portalTrust: string | null;
  postedAt: string | null; firstSeenAt: string; lastSeenAt: string;
  descLen: number; matchCount: number; category: string;
};

type JobsPage = { items: JobRow[]; total: number; page: number; limit: number };

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDate(d: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" });
}

const TRUST_COLOR: Record<string, string> = { high: T.ok, medium: T.warn, low: T.err };
const CAT_COLOR: Record<string, string> = {
  "AI/ML/LLM": T.tealSoft, "Engineering Manager": "#A78BFA",
  "Architect": T.info, "DevOps/SRE/Cloud": T.warn,
};

function StatChip({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <div style={{ background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "8px",
      padding: "10px 14px", flex: "1 1 130px" }}>
      <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
        letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "4px" }}>{label}</div>
      <div style={{ fontFamily: T.mono, fontSize: "18px", fontWeight: 700,
        color: accent ?? T.t0, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

// ── Inventory summary ──────────────────────────────────────────────────────

function InventorySummary() {
  const token = useAuthStore((s) => s.token);
  const q = useQuery<Inventory>({
    queryKey: ["admin-jd-inventory"],
    queryFn: async () => { const { data } = await axios.get("/api/admin/jd-inventory"); return data; },
    enabled: !!token, retry: false, staleTime: 120_000,
  });

  if (q.isLoading) return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.t3 }}>Loading inventory…</p>;
  if (q.isError)   return <p style={{ fontFamily: T.body, fontSize: "12px", color: T.err }}>Failed to load inventory.</p>;
  if (!q.data)     return null;

  const d = q.data;
  const enrichPct = d.activeJobs > 0 ? Math.round((d.enrichedJobs / d.activeJobs) * 100) : 0;
  const embedPct  = d.activeJobs > 0 ? Math.round((d.embeddingCoverage / d.activeJobs) * 100) : 0;

  return (
    <div style={{ marginBottom: "20px" }}>
      {/* Top stats row */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "12px" }}>
        <StatChip label="Total jobs"    value={d.totalJobs} />
        <StatChip label="Active"        value={d.activeJobs}       accent={T.ok} />
        <StatChip label="Inactive"      value={d.inactiveJobs}     accent={T.t3} />
        <StatChip label="Enriched JDs"  value={`${d.enrichedJobs} (${enrichPct}%)`} accent={d.enrichedJobs > 0 ? T.tealSoft : T.err} />
        <StatChip label="Embeddings"    value={`${d.embeddingCoverage} (${embedPct}%)`} accent={embedPct > 80 ? T.ok : T.warn} />
        <StatChip label="Fresh (7d)"    value={d.freshJobs}        accent={d.freshJobs > 0 ? T.ok : T.t3} />
      </div>

      {/* Sources + Trust side-by-side */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
        {/* By source */}
        <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "8px", padding: "12px 14px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>By Source</div>
          {d.bySources.slice(0, 8).map(({ source, count }) => {
            const pct = d.activeJobs > 0 ? Math.round((count / d.activeJobs) * 100) : 0;
            return (
              <div key={source} style={{ marginBottom: "7px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "2px" }}>
                  <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.t2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "65%" }}>{source}</span>
                  <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.t1 }}>{count}</span>
                </div>
                <div style={{ height: "2px", background: T.b1, borderRadius: "1px" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: T.teal, borderRadius: "1px" }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Top companies */}
        <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "8px", padding: "12px 14px" }}>
          <div style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>Top Companies</div>
          {d.topCompanies.map(({ company, count }) => (
            <div key={company} style={{ display: "flex", justifyContent: "space-between",
              padding: "3px 0", borderBottom: `1px solid ${T.b0}` }}>
              <span style={{ fontFamily: T.body, fontSize: "11px", color: T.t1,
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "75%" }}>{company}</span>
              <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2,
                fontVariantNumeric: "tabular-nums" }}>{count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Jobs table ─────────────────────────────────────────────────────────────

export function AdminJobs() {
  const token = useAuthStore((s) => s.token);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debSearch, setDebSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const debRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const q = useQuery<JobsPage>({
    queryKey: ["admin-jobs", debSearch, page],
    queryFn: async () => {
      const { data } = await axios.get("/api/admin/jobs", {
        params: { page, limit: 50, search: debSearch },
      });
      return data;
    },
    enabled: !!token, retry: false,
  });

  const handleSearch = (v: string) => {
    setSearch(v); setPage(1);
    if (debRef.current !== null) clearTimeout(debRef.current);
    debRef.current = setTimeout(() => setDebSearch(v), 350);
  };

  const totalPages = q.data ? Math.ceil(q.data.total / q.data.limit) : 0;

  return (
    <div>
      <InventorySummary />

      {/* Search */}
      <div style={{ marginBottom: "12px" }}>
        <input type="text" placeholder="Search title or company…" value={search}
          onChange={(e) => handleSearch(e.target.value)}
          style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px",
            background: T.s2, border: `1px solid ${T.b1}`, borderRadius: "6px",
            color: T.t0, fontFamily: T.body, fontSize: "13px", outline: "none" }} />
      </div>

      {q.isLoading && <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t3 }}>Loading…</p>}
      {q.isError   && <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err }}>Failed to load jobs.</p>}

      {q.data && (
        <>
          <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t3,
            letterSpacing: "0.10em", textTransform: "uppercase", marginBottom: "8px" }}>
            {q.data.total} job{q.data.total !== 1 ? "s" : ""}{debSearch ? ` matching "${debSearch}"` : ""}
          </div>

          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", overflow: "hidden" }}>
            {/* Header */}
            <div style={{ display: "grid",
              gridTemplateColumns: "1fr 100px 90px 50px 50px 60px 60px 52px",
              padding: "8px 16px", borderBottom: `1px solid ${T.b0}`,
              fontFamily: T.mono, fontSize: "9px", color: T.t3,
              letterSpacing: "0.10em", textTransform: "uppercase" }}>
              <span>Title / Company</span>
              <span>Category</span>
              <span>Source</span>
              <span style={{ textAlign: "center" }}>JD</span>
              <span style={{ textAlign: "center" }}>Trust</span>
              <span>Seen</span>
              <span style={{ textAlign: "right" }}>Matches</span>
              <span style={{ textAlign: "right" }}>Active</span>
            </div>

            {q.data.items.length === 0 && (
              <div style={{ padding: "24px 16px", fontFamily: T.body, fontSize: "13px",
                color: T.t3, textAlign: "center" }}>No jobs found.</div>
            )}

            {q.data.items.map((j, i) => (
              <div key={j.id} onClick={() => setSelectedId(j.id)}
                style={{ display: "grid",
                  gridTemplateColumns: "1fr 100px 90px 50px 50px 60px 60px 52px",
                  padding: "9px 16px",
                  borderBottom: i < q.data!.items.length - 1 ? `1px solid ${T.b0}` : "none",
                  cursor: "pointer",
                  background: selectedId === j.id ? T.s2 : "transparent",
                  transition: "background 0.1s" }}
                onMouseEnter={(e) => { if (selectedId !== j.id) (e.currentTarget as HTMLDivElement).style.background = T.s2; }}
                onMouseLeave={(e) => { if (selectedId !== j.id) (e.currentTarget as HTMLDivElement).style.background = "transparent"; }}
              >
                <div style={{ overflow: "hidden" }}>
                  <div style={{ fontFamily: T.body, fontSize: "12px", color: T.t0, fontWeight: 600,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.title}</div>
                  <div style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.company}</div>
                </div>
                <span style={{ fontFamily: T.mono, fontSize: "9px",
                  color: CAT_COLOR[j.category] ?? T.t3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  alignSelf: "center" }}>{j.category}</span>
                <span style={{ fontFamily: T.mono, fontSize: "9px", color: T.t3,
                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  alignSelf: "center" }}>{j.source.replace("vps-", "").replace("extension-", "ext:")}</span>
                <span style={{ fontFamily: T.mono, fontSize: "10px", textAlign: "center",
                  color: j.descLen >= 250 ? T.ok : j.descLen > 0 ? T.warn : T.err,
                  alignSelf: "center" }}>
                  {j.descLen >= 250 ? "✓" : j.descLen > 0 ? "~" : "✗"}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "10px", textAlign: "center",
                  color: TRUST_COLOR[j.portalTrust ?? ""] ?? T.t3, alignSelf: "center" }}>
                  {j.portalTrust?.[0]?.toUpperCase() ?? "?"}
                </span>
                <span style={{ fontFamily: T.mono, fontSize: "10px", color: T.t2,
                  alignSelf: "center" }}>{fmtDate(j.firstSeenAt)}</span>
                <span style={{ fontFamily: T.mono, fontSize: "12px", textAlign: "right",
                  color: j.matchCount > 0 ? T.tealSoft : T.t3,
                  fontVariantNumeric: "tabular-nums", alignSelf: "center" }}>{j.matchCount}</span>
                <span style={{ fontFamily: T.mono, fontSize: "11px", textAlign: "right",
                  color: j.isActive ? T.ok : T.err, alignSelf: "center" }}>
                  {j.isActive ? "✓" : "✗"}
                </span>
              </div>
            ))}
          </div>

          {totalPages > 1 && (
            <div style={{ display: "flex", gap: "8px", alignItems: "center",
              marginTop: "12px", justifyContent: "flex-end" }}>
              <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page <= 1 ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page <= 1 ? "default" : "pointer" }}>‹ Prev</button>
              <span style={{ fontFamily: T.mono, fontSize: "11px", color: T.t2 }}>{page} / {totalPages}</span>
              <button type="button" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}
                style={{ padding: "5px 10px", background: T.s2, border: `1px solid ${T.b1}`,
                  borderRadius: "5px", color: page >= totalPages ? T.t3 : T.t0,
                  fontFamily: T.body, fontSize: "12px", cursor: page >= totalPages ? "default" : "pointer" }}>Next ›</button>
            </div>
          )}
        </>
      )}

      {selectedId && <JobDetail jobId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}
